/**
 * voice-v8 — User-Settings (Tag 19, docs/archiv/PLAN.md §9 W4 Tag 19).
 *
 * ⚠️ 23.09.2026, Ticket 2511: Der SERVER ist führend. Der LocalStorage ist
 *    nur noch der Zwischenspeicher, aus dem die Oberfläche sofort liest und
 *    mit dem sie ohne Netz weiterarbeitet. Jede Änderung geht über
 *    `speichereEinstellungen` an den Server und wird bestätigt
 *    (einstellungen-abgleich.ts); beim Start und beim Zurückkehren in die
 *    App kommt der Serverstand zurück. Welche Werte nur für DIESES Gerät
 *    gelten, steht in shared/einstellungen-felder.ts.
 *
 * API-Keys gehören NICHT hier rein — die bleiben server-side in `.env`.
 */
import {
  MEDITATIONS_STIL_VORGABE,
  meditationsStilAus,
  type MeditationsStil,
} from "../../shared/meditations-stil.js";
import {
  DENKSTUFE_TIEF_VORGABE,
  istTiefeDenkstufe,
  type TiefeDenkstufe,
} from "../../shared/denkstufe.js";
import {
  ABO_MODELLE,
  aboWahlAus,
  istAboWahl,
  starkesModellAus,
  warmEtikett,
  type AboWahl,
  type StarkesModell,
} from "../../shared/abo-modelle.js";
import { claudeName, nameFuerModell } from "../../shared/modell-namen.js";
import {
  GEMINI_WERKZEUG,
  geminiBackupAus,
  MODELL_GUENSTIG,
  MODELL_SCHNELL,
  MODELL_STARK,
  ohneFable,
} from "../../server/models.js";
import { tageBereinigen } from "../../server/auto-archiv.js";
import { EINSTELLUNGEN_SCHLUESSEL } from "../../shared/einstellungen-felder.js";
import { istJahreszeitWahl, JAHRESZEIT_VORGABE, type JahreszeitWahl } from "../../shared/jahreszeit.js";
import { istElevenStimmKennung, istMeditationsStimme } from "../../shared/meditations-stimme.js";
import { ausfuehrlichkeitWahlGueltig, schmiedStimmeAus, stimmeWahlGueltig, tempoWahlGueltig, type Ausfuehrlichkeit } from "../../shared/schmied.js";
import { istEmojiStufe, type EmojiStufe } from "../../shared/emoji-stufe.js";
import {
  RUHEZEIT_AB,
  RUHEZEIT_BIS,
  TAGESLIMIT,
  anrufStunde,
  anrufTageslimit,
} from "../../shared/anruf-grenzen.js";
import { vorleseTempo } from "../../shared/vorlese-tempo.js";
import { NACHLAUSCH_VORGABE_MS, nachlauschFenster } from "../../shared/nachlausch.js";
import { autoMikroStufe, type AutoMikroStufe } from "../../shared/mikro-nach-antwort.js";
import { SENDE_STILLE_STUFEN, sendeStilleLesen, STILLE_AUTO_MS } from "../../shared/stille-auto.js";
import {
  ANTWORT_TIEFE_VORGABE,
  istAntwortTiefe,
  type AntwortTiefe,
} from "../../shared/antwort-tiefe.js";
import {
  ergebnis,
  merkeAenderungen,
  sendeVorgemerktes,
  type SpeicherErgebnis,
} from "./einstellungen-ausstehend.js";

export type VoiceGender = "male" | "female" | "neutral";

interface VoiceOption {
  /** Optionales ElevenLabs-Modell (z.B. eleven_v3 fuer v3-Klone). */
  model?: string;
  id: string;
  label: string;
  description?: string;
  /**
   * Geschlechts-Gruppierung für den Stimmen-Picker.
   * "neutral" = eigene Klon-Stimme / nicht-binär.
   */
  gender: VoiceGender;
}

interface ModelOption {
  id: string;
  label: string;
  description?: string;
}

/** 2026-07-23 (Martin, "eine Logik"): Die BEWUSST gewaehlte Vorlese-Stimme
 *  (readVoiceChosen + voiceId) gilt ueberall als Start-Stimme der
 *  Vorlese-Player (Buch/Essenz/HD/...). Google-Erzaehler (de-DE-...) zaehlt
 *  nicht — das ist der Legacy-Erzaehler-Fallback. Spiegelbild der
 *  Server-Logik readChosenReadVoice (server/index.ts). */
export function chosenReadVoice(): string | null {
  // 2026-07-23 (Martin, "eine Stimme fuer alles"): auch Gemini-/Google-
  // Stimmen (de-DE-*) sind vollwertige Vorlese-Wahlen — der fruehere
  // Filter machte sie unsichtbar und erzeugte zwei Stimm-Wahrheiten.
  const st = loadSettings();
  if (
    st.readVoiceChosen === true &&
    typeof st.voiceId === "string" &&
    st.voiceId.trim()
  ) {
    return st.voiceId;
  }
  return null;
}

/**
 * Die eine Stimme fürs Vorlesen. (04.09.2026)
 *
 * ## Warum es diese Funktion gibt
 *
 * Martin: *„In Mein Alltag oder Mein Leben hört man allerdings noch diese
 * alte Stimme."*
 *
 * Nachgesehen: Über die Vorlese-Stimme entschieden VIER Stellen, und sie
 * waren nicht einig.
 *
 *     app.ts (3×)        chosenReadVoice() || narratorVoiceId || DEFAULT
 *     alltag-view.ts     st.voiceId          ← fragt gar nicht erst
 *
 * Die Kette stand dreimal wortgleich im Code — und der Alltag hatte seine
 * eigene, kürzere. Wer im Vorlese-Wähler eine Stimme setzt, schreibt
 * `narratorVoiceId`; wer in den Einstellungen wählt, schreibt `voiceId`.
 * Welche gewinnt, hing davon ab, welchen Weg man gerade benutzte.
 *
 * ⚠️ Dasselbe Muster wie bei den zwei Ton-Ringen und den vier
 *    Aufnahme-Fristen: Nicht ein Fehler, sondern eine fehlende gemeinsame
 *    Stelle. Wer hier eine fünfte Kette baut, fängt von vorne an.
 */
export const ERZAEHLER_RUECKFALL = "de-DE-Chirp3-HD-Charon";

export function vorleseStimme(): string {
  const st = loadSettings();
  return chosenReadVoice() || st.narratorVoiceId || ERZAEHLER_RUECKFALL;
}

/** 2026-07-23 (Martin): Gemini-Stimmen als vollwertige VORLESE-Wahl —
 *  gleiche Klangfamilie wie der Anruf. Bewusst NICHT in VOICE_OPTIONS
 *  (deren [0] ist der Default; app.ts fuehrt Google-Stimmen im Erzaehler-
 *  Picker separat), sondern nur im Einstellungs-Picker ergaenzt. */
export const GEMINI_READ_VOICES: ReadonlyArray<VoiceOption> = [
  /*
   * ⚠️ 09.09.2026 — VON VIER AUF ZWEI (Martin: „Von Gemini auch nur mehr
   *    zwei Stimmen").
   *
   * Hier standen Aoede, Charon, Kore und Puck. Aoede und Charon sind
   * dieselben zwei, die auch `GOOGLE_NARRATOR_VOICES` fuehrt — zwei Listen,
   * die dasselbe meinen, gingen auseinander. Jetzt decken sie sich.
   *
   * Ordnung f, m — im Zweierraster steht die Frau links (Martins Wunsch,
   * 2026-07-24).
   */
  {
    id: "de-DE-Chirp3-HD-Aoede",
    label: "Aoede",
    description: "Weiblich, freundlich — wie im Anruf",
    gender: "female",
  },
  {
    id: "de-DE-Chirp3-HD-Charon",
    label: "Charon",
    description: "Männlich, warm, ruhig",
    gender: "male",
  },
];


/**
 * Die Stimmen, die zur Auswahl stehen. (09.09.2026 zusammengeraeumt)
 *
 * Martin: *„Ich möchte gerne die Stimmen zusammenräumen"* — und nach dem
 * Hören der Proben: *„Bitte Grace und Iris und Daniel und Nathan für
 * Soniox."*
 *
 * ## Woher die neuen Namen kommen
 *
 * Soniox listet seine eingebauten Stimmen NICHT ueber `/v1/voices` — dort
 * steht nur der eigene Klon. Also durchprobiert: von 30 Namen antworten
 * neben den bekannten auch **Grace, Nora, Ruby, Hazel, Iris, Lena**
 * (weiblich). Martin hat die Proben gehoert und gewaehlt.
 *
 * ⚠️ Ich kann nicht hoeren. Ueber Klang entscheidet hier niemand ausser
 *    Martin; was frueher an Beschreibungen dastand, war teilweise geraten.
 *    Deshalb sind die Beschreibungen knapp und nennen nur, was belegt ist.
 *
 * ## Was rausgeflogen ist, und wohin die Einstellung umzieht
 *
 * Maya, Emma, Adrian, Jack (Soniox) und Brian (ElevenLabs). Wer eine davon
 * gespeichert hat, wird in `loadSettings` auf die naechstliegende Stimme
 * gleichen Geschlechts umgezogen — sonst stuende die Auswahl leer, waehrend
 * im Hintergrund weiter die alte Stimme spraeche.
 *
 * ⚠️ Reihenfolge f, m, f, m — im Zweierraster stehen damit die Frauen links
 *    und die Maenner rechts (Martins Wunsch vom 24.07.).
 */
export const VOICE_OPTIONS: ReadonlyArray<VoiceOption> = [
  {
    id: "soniox:Grace",
    label: "Grace",
    description: "Weiblich",
    gender: "female",
  },
  {
    id: "soniox:Daniel",
    label: "Daniel",
    description: "Voll, poliert, männlich",
    gender: "male",
  },
  {
    id: "soniox:Iris",
    label: "Iris",
    description: "Weiblich",
    gender: "female",
  },
  {
    id: "soniox:Nathan",
    label: "Nathan",
    description: "Klar, tief, männlich",
    gender: "male",
  },
  // 15.10.2026, Martin: die Stimme der Meditation soll auch zum Vorlesen wählbar sein.
  {
    id: "soniox:Marlene",
    label: "Marlene",
    description: "Ruhig, deutsch, weiblich",
    gender: "female",
  },
  {
    id: "soniox:Konrad",
    label: "Konrad",
    description: "Sanft, deutsch, männlich",
    gender: "male",
  },
  /*
   * ⚠️ 09.09.2026 — ELEVENLABS WAR ZU UNRECHT ABGESCHALTET.
   *
   * `TTS_DISABLE_ELEVENLABS=1` steht seit pre.1413 mit der Begruendung
   * „leeres Kontingent". Selbst nachgemessen: eine Probe-Vertonung kommt mit
   * HTTP 200 und 25.539 Bytes MP3 in 2,2 s zurueck. Was zur Fehldeutung
   * fuehrte: `/v1/user` und `/v1/voices` antworten mit 401 — dem Schluessel
   * fehlt die Leseberechtigung fuers KONTO, nicht das Guthaben. Aus dem 401
   * wurde damals „leer" geschlossen.
   */
  {
    id: "EXAVITQu4vr4xnSDxMaL",
    label: "Sarah",
    description: "Weich, weiblich",
    gender: "female",
  },
  {
    id: "JBFqnCBsd6RMkjVDRZzb",
    label: "George",
    description: "Warm, britisch, männlich",
    gender: "male",
  },
  /*
   * Die eigene, geklonte Stimme. (10.09.2026)
   *
   * Martin: *„ob zwei Eleven Labs Stimmen … schon im System sind und meine
   * eigene als dritte."* Sie funktionierte längst (im Browser geprüft: 200,
   * 22 kB Ton), stand aber in keiner Liste — erreichbar war sie nur über die
   * Umgebungsvariable, also gar nicht zum Auswählen.
   *
   * ⚠️ Als WAHL ist sie richtig; als ERSATZ war sie der Fehler vom selben
   *    Tag: Bei einem Soniox-Timeout sprang sie mitten im Text ein, und
   *    plötzlich las er sich selbst vor (siehe server/soniox-tts.ts,
   *    rueckfallFuer). Gewählt heißt gewollt — untergeschoben nie.
   *
   * ⚠️ `gender: "neutral"` ist Absicht: Eine geklonte Stimme ist kein
   *    Ersatz für jemand anderen, und der Rückfall soll sie nie über die
   *    Geschlechts-Regel erwischen.
   */
  {
    id: "hjWcVMU82C7uXy86PA2K",
    label: "Deine Stimme",
    description: "Dein eigener Stimm-Klon",
    gender: "neutral",
  },
];

/**
 * Stimmen, die es nicht mehr gibt — und wohin ihre Nutzer umziehen.
 *
 * ⚠️ Gleiches Geschlecht, sonst spricht ploetzlich jemand anderes. Genau das
 *    ist am 25.07. schon einmal passiert (Martin: *„ploetzlich wieder eine
 *    Frauenstimme"*), damals durch eine Migration, die ihr Ergebnis nicht
 *    zurueckschrieb.
 */
export const STIMMEN_UMZUG: Readonly<Record<string, string>> = {
  "soniox:Maya": "soniox:Grace",
  "soniox:Emma": "soniox:Iris",
  "soniox:Adrian": "soniox:Daniel",
  "soniox:Jack": "soniox:Nathan",
  // Brian (ElevenLabs, maennlich) -> George (ElevenLabs, maennlich)
  nPczCjzI2devNBz1zQrb: "JBFqnCBsd6RMkjVDRZzb",
};


/**
 * Pre.544: Gratis Google-Chirp3-HD-Stimmen als Erzähler-Optionen für die
 * Biografie-Vorlesung. Die `id` ist der volle Google-Voice-Name (z.B.
 * "de-DE-Chirp3-HD-Aoede") — der Server (tools/tts-render.ts isGoogleVoice)
 * erkennt das Muster und rendert direkt via Google, ohne ElevenLabs-Kosten.
 * Diese Stimmen sind dieselbe Voice-Family wie Gemini Live (siehe app.ts
 * Chirp3-HD-Map) — hochwertig und kostenlos bis 1 Mio Zeichen/Monat.
 *
 * Geschlechter folgen Googles dokumentierten Chirp3-HD-Charakteren:
 *   weiblich: Aoede, Kore, Zephyr, Achernar (sanft)
 *   männlich: Charon, Puck, Achird, Algenib
 */
export const GOOGLE_NARRATOR_VOICES: ReadonlyArray<VoiceOption> = [
  // 2026-06-29 (Martin): bewusst auf ZWEI kuratierte Stimmen reduziert (1 m / 1 w)
  // — wie bei Sesame. Weniger Auswahl, klarer gefuehrt. Die Realtime-/Pipeline-
  // Maps im Agent kennen weiterhin die alten Namen (Superset), falls Altwerte
  // in users.settings stehen.
  {
    id: "de-DE-Chirp3-HD-Charon",
    label: "Charon",
    description: "Männlich, warm, ruhig",
    gender: "male",
  },
  {
    id: "de-DE-Chirp3-HD-Aoede",
    label: "Aoede",
    description: "Weiblich, freundlich",
    gender: "female",
  },
];

/**
 * Was im Abo antwortet — die einzigen drei, die OHNE API-Guthaben laufen.
 *
 * 2026-08-27 (Martin: "Dann bitte nur Sonnet, Opus und Haiku lassen. Sonst
 * wuerde das Ganze keinen Sinn haben, oder?"). Genau so: die Gemini-Eintraege
 * aus MODEL_OPTIONS gehen ueber die API und damit ueber Guthaben — gegen seine
 * stehende Regel.
 *
 * "auto" steht bewusst zuerst und ist die Vorgabe: es ist das Verhalten seit
 * dem 06.07., das er selbst gewuenscht hat.
 */
/* ⚠️ Die Liste steht EINMAL, in shared/abo-modelle.ts — sonst faellt ein
   neues Modell wieder still in den Sonnet-Zweig (27.08. Haiku, 30.08. die
   Selbstauskunft). Hier nur durchgereicht. */
export { ABO_MODELLE, istAboWahl, warmEtikett, type AboWahl };

export const MODEL_OPTIONS: ReadonlyArray<ModelOption> = [
  {
    // 15.10.2026 (Martin: „Warum steht Haiku 4.5, wenn es gar kein Haiku 4.5 gibt?“):
    // aus dem Register wie Sonnet und Opus – hier stand die alte Kennung mit festem Namen.
    id: MODELL_GUENSTIG,
    label: `Claude ${claudeName(MODELL_GUENSTIG)}`,
    description: "Schnell + günstig — Standard",
  },
  {
    // 28.09.2026: wie Opus aus dem Register — hier stand „claude-sonnet-5".
    id: MODELL_SCHNELL,
    label: `Claude ${claudeName(MODELL_SCHNELL)}`,
    description: "Ausgewogen, gut für Tools — 1M Context",
  },
  {
    // ⚠️ 23.09.2026: aus dem Register, nicht als Literal — hier stand
    //    „claude-opus-5" / „Claude Opus 5", und der Register-Wächter sieht
    //    frontend/ nicht. Beim nächsten Wechsel wäre es stehen geblieben.
    id: MODELL_STARK,
    label: `Claude ${claudeName(MODELL_STARK)}`,
    description: "Stärkstes Modell, beste Reasoning — 1M Context",
  },
  {
    id: "gemini-2.5-pro",
    label: nameFuerModell("gemini-2.5-pro"),
    description: "Google — mächtig, Tool-Use, lange Kontexte",
  },
  {
    id: "gemini-2.5-flash",
    label: nameFuerModell("gemini-2.5-flash"),
    description: "Google — schnell + günstig, Werkzeuge",
  },
  {
    // ⚠️ 14.09.2026: Hier stand gemini-3.7-flash — die ABGELOESTE Fassung.
    // Das Register (server/models.ts) faehrt seit pre.2869 3.8 Flash (GA seit
    // 02.09., per Web-Suche belegt). Wer hier 3.7 auswaehlte, bekam ein
    // anderes Modell als die Automatik. Der Name kommt jetzt aus der
    // Ableitung, damit das nicht wieder auseinanderlaeuft.
    id: "gemini-3.8-flash",
    label: nameFuerModell("gemini-3.8-flash"),
    description: "Google — schnell, Werkzeuge oft unsicher",
  },
  {
    id: "gemini-2.5-flash-lite",
    label: nameFuerModell("gemini-2.5-flash-lite"),
    description: "Google — sehr günstig",
  },
  // Pre.433: Gemini 3.5 Flash (I/O 2026, 19.05.) — neueste Text/Agent-Gen.
  {
    id: "gemini-3.5-flash",
    label: nameFuerModell("gemini-3.5-flash"),
    description: "Google — neueste Generation",
  },
  // Hinweis: GPT-Modelle (gpt-5.5/gpt-4o) wurden entfernt — detectProvider routet
  // alles ohne „gemini-"-Präfix nach Anthropic, eine gpt-ID lief also ins Leere.
  // Der einzige OpenAI-Pfad bleibt der interne Quota-Fallback (gpt-4o-mini).
];

export type SttSensitivity = "quiet" | "normal" | "loud";

type ResponseStyle = "active" | "passive";

/**
 * Push-to-Talk Antwort-Modus (pre.145).
 *   - silent: nur Text-Bubble.
 *   - tts:    Bot-Antwort wird automatisch vorgelesen (playTts).
 *   - call:   PTT-Tap startet stattdessen einen Voice-Call.
 *   - lock:   Long-Press öffnet Daueraufnahme (Telegram-Style).
 */
export type PttMode = "silent" | "tts" | "call" | "lock";

/**
 * Pre.180: TTS-Provider-Wahl.
 *  - "auto":       ElevenLabs primary, Google-Fallback bei Quota/401.
 *  - "elevenlabs": Strikt ElevenLabs (kein Fallback — Bot wird stumm bei Quota).
 *  - "google":     Strikt Google Cloud TTS (Wavenet/Neural2) — gratis bis
 *                  4 Mio Zeichen/Monat, keine ElevenLabs-Quota-Sorgen.
 */
export type TtsProvider = "auto" | "elevenlabs" | "google";

/**
 * Pre.186: Realtime-Voice-Listen pro Provider. Diese Stimmen werden vom
 * jeweiligen Realtime-Backend (Gemini Live / OpenAI gpt-4o-realtime)
 * nativ produziert — komplett getrennt vom ElevenLabs-Picker (`VOICE_OPTIONS`).
 */
interface RealtimeVoiceOption {
  id: string;
  label: string;
  description: string;
  /**
   * Pre.664: Geschlecht — explizit statt aus der Beschreibung geraten. Damit
   * teilen sich Realtime-Stimmen (Gemini/OpenAI) DASSELBE Fundament wie die
   * Vorlese-/ElevenLabs-Stimmen (♀/♂-Gruppen, einheitliche Picker).
   */
  gender: VoiceGender;
}

/**
 * Pre.208: Auswählbare Realtime-Modelle pro Provider. "Latest" ist
 * unsere kuratierte stabile Empfehlung; die preview-Varianten sind zum
 * Testen neuer Funktionen drin.
 */
interface RealtimeModelOption {
  id: string;
  label: string;
  description: string;
  provider: "gemini" | "openai";
}

/**
 * Die Stufen für „Absenden nach Stille" — EINE Quelle für die Einstellungen
 * und das Blatt der Antwortlänge. (23.09.2026, Ticket 2509)
 *
 * Die Liste selbst steht seit 30.09.2026 (Ticket 2842: 4 und 5 s entfallen)
 * in shared/stille-auto.ts, denn der Server liest den gespeicherten Wert durch
 * dieselbe Datei (sendeStilleLesen). Hier nur weitergereicht, damit die
 * Aufrufer ihren Import behalten.
 *
 * ⚠️ Die Stufen wechselt man in der Fußzeile und im Blatt der Antwortlänge,
 *    nicht mehr im Mikrofon-Fenster: dort steht seit 30.09.2026 nur noch die
 *    Anzeige (ptt-modal.ts), ohne Knopf.
 */
export { SENDE_STILLE_STUFEN };

/**
 * Die Stufen samt „Auto" am Ende — nur wo Auto zur Wahl steht (Konto 1, wie
 * der Server es erlaubt: server/stille-auto-routen.ts). (Ticket 2763)
 */
export function sendeStilleStufen(mitAuto: boolean): readonly number[] {
  return mitAuto ? [...SENDE_STILLE_STUFEN, STILLE_AUTO_MS] : SENDE_STILLE_STUFEN;
}

/**
 * Wann das Mikro nach Vesperas Antwort von selbst aufgeht — EINE Quelle für
 * die Einstellungen und den Knopf im Mikrofon-Fenster. (23.09.2026, Ticket 2403)
 *
 * Martin, 20.09. 21:25: *„dass nach einem Austausch … das System
 * automatisch, nachdem ich eine Antwort bekommen habe, das Mikro wieder
 * öffnet"*. Bis dahin ging es nur nach einer Frage auf.
 *
 * 07.10.2026 (Ticket 3048): die dritte Stufe heißt jetzt „Jev entscheidet“
 * ("jev") statt „immer“ — Martin: „immer“ war nie so gemeint. Stufen, Umstieg
 * und die EINE Entscheidung nach dem Vorlese-Ende stehen in
 * shared/mikro-nach-antwort.ts; der Server liest dieselben Stufen.
 */
export { autoMikroStufe, type AutoMikroStufe };
export {
  AUTO_MIKRO_STUFEN,
  autoMikroStufenFuer,
  autoMikroStufeFuerKonto,
  JEV_KURZ_MS,
  mikroNachAntwort,
  mikroNachAntwortZeile,
  naechsteAutoMikro,
} from "../../shared/mikro-nach-antwort.js";

// Ticket 2668: das Nachlausch-Fenster steht in shared/nachlausch.ts — der
// Server lässt dieselben Stufen zu. Weitergereicht für die bestehenden Importe.
export { NACHLAUSCH_STUFEN, NACHLAUSCH_VORGABE_MS, nachlauschFenster } from "../../shared/nachlausch.js";

export const GEMINI_REALTIME_MODELS: ReadonlyArray<RealtimeModelOption> = [
  {
    /*
     * 23.09.2026 — Martin: „Gemini 3.8 Live ist da. Bitte ersetze unser altes
     * Gemini Realtime." Leer = Standard des Agenten (agent/anruf-modell.ts),
     * dort steht auch die Messung (je drei Läufe gegen 3.1).
     */
    id: "",
    label: `${nameFuerModell("gemini-3.8-live")} — empfohlen`,
    description:
      "Stabil, erledigt Nachschlagen im Hintergrund, während es weiterspricht, und kennt das Gespräch auch nach einer Unterbrechung.",
    provider: "gemini",
  },
  {
    // pre.909 (Martin: „den besten 3.1 verwenden"): EIN klares, bestes 3.1.
    // Der frühere zweite 3.1-Eintrag („#compress / LANGE Gespräche") war seit
    // pre.900 ein No-Op (Kompression auf 3.1 aus; der 9-Minuten-Fix ist der
    // Plugin-Patch, nicht die Kompression) → entfernt, weil er exakt wie dieses
    // hier lief und nur verwirrte. „#compress" wird im Agent weiterhin auf
    // plain 3.1 normalisiert (alte gespeicherte Auswahl bleibt also funktionsfähig).
    //
    // ⚠️ 23.09.2026: nur noch als BEWUSSTE Wahl („#bisher"). Das schlichte
    //    „gemini-3.1-flash-live-preview" war seit pre.972 die einzige Wahl —
    //    ein gespeicherter Wert davon ist ein Rest, keine Entscheidung, und
    //    zieht auf den Standard um (loadSettings und agent/anruf-modell.ts).
    id: "gemini-3.1-flash-live-preview#bisher",
    label: `${nameFuerModell("gemini-3.1-flash-live-preview")} — bisher`,
    description:
      "Das bisherige Modell: gut 0,3 Sekunden schneller bis zum ersten Ton, dafür blockiert es beim Nachschlagen.",
    provider: "gemini",
  },
  // Die Gemini-2.5-native-audio-Einträge wurden entfernt: nur 3.1 ist nutzbar,
  // und der 9-Minuten-Fix läuft über den Plugin-Reconnect (nicht über 2.5-Kompression).
];

/**
 * Ticket 745: einmalige Uebernahme des alten localStorage-Werts. Nur relevant
 * fuer Geraete, auf denen der Haken vor dem Umzug in die DB gesetzt war.
 */
function leseAltenAlwaysRecord(): boolean {
  try {
    return localStorage.getItem("voice-v8.alwaysRecord") === "1";
  } catch {
    return false;
  }
}

/**
 * Der alte Geräteschlüssel für „Getipptes vorlesen" (bis 23.09.2026).
 * undefined = auf diesem Gerät nie gesetzt (dann gilt die Vorgabe, seit
 * 15.10.2026 AN); "0" = hier bewusst ausgeschaltet und bleibt aus. Gelesen,
 * solange das Konto den Wert noch nicht kennt — und vom Abgleich.
 */
export function leseAltesTippenVorlesen(): boolean | undefined {
  try {
    const v = localStorage.getItem("voice-v8.tippen-vorlesen");
    return v === null ? undefined : v === "1";
  } catch {
    return undefined;
  }
}

/** Achtsamkeits-Wächter aus dem Zwischenspeicher — nur bekannte Formen. */
function achtsamkeitAus(roh: unknown): VoiceV8Settings["achtsamkeit"] {
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return undefined;
  const a = roh as Record<string, unknown>;
  const kategorien: Record<string, boolean> = {};
  if (a.kategorien && typeof a.kategorien === "object") {
    for (const [k, v] of Object.entries(a.kategorien as Record<string, unknown>)) {
      if (typeof v === "boolean") kategorien[k] = v;
    }
  }
  return {
    ...(typeof a.aktiv === "boolean" ? { aktiv: a.aktiv } : {}),
    ...(Object.keys(kategorien).length > 0 ? { kategorien } : {}),
    ...(typeof a.signal === "string" && a.signal ? { signal: a.signal } : {}),
  };
}

export const GEMINI_REALTIME_VOICES: ReadonlyArray<RealtimeVoiceOption> = [
  /*
   * 2026-09-10 (Martin): *„bei den Bot-Stimm-Einstellungen auch nur mehr die
   * Gemini, AOE und Charon."*
   *
   * Am 27.07. standen hier wieder VIER — damit die Liste zum Vorlesen passte.
   * Genau die ist am 09.09. auf zwei geschrumpft (GEMINI_READ_VOICES); jetzt
   * zieht der Anruf nach. Eine Wahl aus zwei, die man beide mag, ist besser
   * als eine aus vier, von denen zwei nie drankommen.
   *
   * ⚠️ Wer Kore oder Puck eingestellt hatte, zieht ueber REALTIME_UMZUG um —
   *    gleiches Geschlecht, sonst spricht ploetzlich jemand anderes.
   */
  { id: "Aoede", label: "Aoede", description: "weiblich, freundlich", gender: "female" },
  { id: "Charon", label: "Charon", description: "männlich, warm & ruhig", gender: "male" },
];

/** Abgelegte Anruf-Stimmen und ihr Nachfolger — gleiches Geschlecht. */
export const REALTIME_UMZUG: Readonly<Record<string, string>> = {
  Kore: "Aoede",
  Puck: "Charon",
};

/**
 * Pre.183 Sprint 1: Voice-Mode-Wahl.
 *  - "pipeline":         klassische STT→LLM→TTS-Kette (Erzähl-Modus, ~2-3s Latenz).
 *  - "gemini-realtime":  Gemini Live API (native voice-zu-voice, ~500ms) — Real Talk.
 * Pre.972: "openai-realtime" entfernt (nie aktiv genutzt, nur 3.1/Gemini nutzbar).
 */
type VoiceMode = "pipeline" | "gemini-realtime";

/** Text/PTT: Auto = Leiter, sonst fest Grok oder Gemini. Live-Anruf bleibt Gemini Live. */
export type ModellBevorzugt = "auto" | "grok" | "grok-schnell" | "gemini";

export interface VoiceV8Settings {
  voiceId: string;
  /** Eigene Erzählerstimme für die Biografie-Vorlesung. Leer = Default-Erzähler. */
  narratorVoiceId: string;
  /** HD-Chart-Ansicht: nur Design (rot), nur Persönlichkeit (schwarz) oder beide. */
  hdChartView: "design" | "personality" | "both";
  model: string;
  /**
   * Optionales Fallback-Modell — wird verwendet wenn das primäre Modell
   * rate-limited oder offline ist (429/503/quota). Leer = kein Fallback.
   */
  fallbackModel: string;
  language: string;
  autoConnect: boolean;
  /** Vorlese-Tempo: 0,7 bis 2,0 in Schritten von 0,1 (shared/vorlese-tempo.ts). Default 1.0. */
  speed: number;
  /** Mikrofon-Empfindlichkeit für die STT-Filter. */
  sttSensitivity: SttSensitivity;
  /** Antwort-Stil im Anruf-Modus: aktiv (begrüßt, plaudert) vs passiv (nur auf Ansprache). */
  responseStyle: ResponseStyle;
  /** Push-to-Talk-Modus (pre.145). */
  pttMode: PttMode;
  /** Pre.180: TTS-Provider-Auswahl. */
  ttsProvider: TtsProvider;
  /** Pre.183: Voice-Mode (Pipeline vs. Realtime). */
  voiceMode: VoiceMode;
  /** Pre.183: Realtime-Stimme (z.B. "Aoede" für Gemini). Leer = Provider-Default. */
  realtimeVoice: string;
  /** Pre.208: Realtime-Modell-ID. Leer = Provider-Default. */
  realtimeModel: string;
  /**
   * Pre.381: CLI-Modell für task_delegate + claude_task_server.
   * "auto" → Tool entscheidet (sonnet als Default).
   * "haiku" | "sonnet" | "opus" → erzwungen.
   * ⚠️ 23.09.2026, Ticket 2516: „fable" ist raus; ein gespeichertes wird
   *    beim Laden Opus (loadSettings).
   */
  cliModel: "auto" | "haiku" | "sonnet" | "opus";
  /**
   * Pre.393: Wo läuft Claude CLI?
   * "server" (Default) — am voice-v8-Server (Files unter /home/pawbot/...)
   * "desktop" — am eigenen Mac via Pawbot Desktop Daemon (lokale Files)
   * Fallback auf "server" wenn Daemon nicht verbunden.
   */
  cliExecutor: "server" | "desktop";
  /**
   * Pre.476: Smart-Router. Bei Auftrags-Intents (Listen, Recherchen, Dokumente,
   * Analysen, Code) wird automatisch an die heavy_task Engine eskaliert
   * (Sonnet 4.6 statt Haiku). Default true. /heavy <prompt> erzwingt immer.
   */
  autoEscalate: boolean;
  /**
   * Pre.476: Qualitäts-Modus.
   * "standard" — eskaliert nur bei klaren Auftrags-Signalen (conf >= 0.75)
   * "premium"  — eskaliert aggressiver (conf >= 0.55), mehr Sonnet-Verbrauch
   */
  qualityMode: "standard" | "premium";
  /** Pre.476: Heavy-Modell für Sonnet/Opus. Default claude-sonnet-4-6. */
  heavyModel: string;
  /**
   * Human Design in der Persona: wenn true, bekommt der Bot einen kompakten
   * HD-Guidance-Block (Typ, Strategie, Autorität, Zentren) und passt WIE er
   * kommuniziert an dein Chart an. Opt-in — Default false.
   */
  useHdInPersona?: boolean;
  /**
   * #183: Emojis in Vesperas Antworten. Opt-in — Default false.
   * Seit Ticket 2736 drei Stufen in diesem einen Feld: false = aus,
   * true = dezent (das bisherige „an"), "reichlich" (shared/emoji-stufe.ts).
   */
  emojisInReplies?: EmojiStufe;
  /**
   * Ticket 2209 (10.09.2026): Darf Vespera aus der Stimme auf die Stimmung
   * schliessen — nicht nur den Klang beschreiben?
   *
   * ⚠️ Vorgabe TRUE, weil Martin ausdruecklich zugestimmt hat. Der Schalter
   *    steht trotzdem da: Eine Zustimmung, die man nicht zuruecknehmen kann,
   *    ist keine. Aus = es bleibt bei „ruhig, klar, monoton".
   */
  stimmungAusStimme?: boolean;
  /**
   * Ticket 2550 (24.09.2026): der Baustein INNEN UND AUSSEN —
   * Selbstermächtigung im Prompt von Chat, Anruf und Morgenmeditation.
   * Martin: *„Der Haltungsbaustein kann gerne auf einen eigenen Schalter
   * kommen."* Vorgabe TRUE (so lief es bisher); ein Kontowert, der Server
   * liest ihn selbst (agent/persona.ts, selbstermaechtigungAn).
   */
  selbstermaechtigung?: boolean;
  /**
   * Ticket 2728 (01.10.2026): „Vespera darf mich anrufen“ — Vorgabe AUS, nur
   * das Hauptkonto sieht den Schalter. Ein Kontowert; der Server liest ihn
   * selbst (server/anruf-ausloesen.ts, anrufErlaubtAus).
   */
  anrufErlaubt?: boolean;
  /**
   * 02.10.2026 (Ticket 2728): Ruhezeit (Stunde 0–23, Wiener Zeit) und
   * Tageslimit (1–20) der Anrufe. Fehlt = Vorgabe 22–5 bzw. 5
   * (shared/anruf-grenzen.ts); `anrufRuheAb === anrufRuheBis` = keine
   * Ruhezeit. Der Server liest sie je Anruf selbst (anrufGrenzenAus).
   */
  anrufRuheAb?: number;
  anrufRuheBis?: number;
  anrufTageslimit?: number;
  /**
   * Ticket 745 (2026-07-27): „Immer aufzeichnen (rund um die Uhr)".
   * Lag bis dahin NUR als localStorage['voice-v8.alwaysRecord'] im WKWebView
   * — bei jeder Neuinstallation der App war der Haken weg (genau das ist
   * Martin am 27.07. passiert). Die native Seite hat nur einen Setter, keinen
   * Getter, kann die Wahrheit also nicht zurueckliefern. Deshalb lebt der
   * Schalter jetzt hier, wie jede andere Einstellung auch, und wird beim
   * Laden an die native Seite nachgereicht.
   */
  alwaysRecord?: boolean;
  /**
   * Nach so vielen Tagen wandert der Chat-Verlauf von selbst ins Archiv.
   * 0 = aus. (Ticket-los, Martin am 22.09.2026.)
   *
   * *„Ich möchte, dass ein Auto-Archiv aktiviert wird, da ich jetzt schon
   * seit Wochen nicht wirklich mehr den Mistkübel verwende. Der Chatverlauf
   * soll dann einfach nach einer gewissen Anzahl von Tagen ins Archiv
   * verschwinden, weil die User in der Regel gar nicht scrollen."*
   *
   * ⚠️ Der Wert wird SERVERSEITIG beim Lesen des Verlaufs gerechnet
   *    (server/auto-archiv.ts). Nichts wird geschrieben, nichts geloescht —
   *    auf 0 stellen holt den ganzen Verlauf im selben Moment zurueck.
   */
  archivTage?: number;
  /**
   * Nach einer Antwort, die mit einer FRAGE endet, das Mikro von selbst
   * oeffnen (Ticket 2403, Martin am 19.09.). Seit 23.09.2026 dreistufig,
   * siehe AutoMikroStufe: aus, nur nach einer Frage, Jev entscheidet (seit
   * 07.10.2026 statt „nach jeder Antwort“, Ticket 3048).
   *
   * ⚠️ Standard AUS. Es ist die Vorstufe zur Freihand-Konversation, und die
   *    ist geparkt („9 Wurzeln bewiesen, erst nach eigenem Test"). Ein
   *    Mikro, das von selbst aufgeht, muss man einschalten wollen.
   */
  autoMikroNachFrage?: AutoMikroStufe;
  /**
   * Ticket 2668: so lange lauscht das von selbst geöffnete Mikro, bevor es
   * ohne Sprache wieder schließt (ms, siehe NACHLAUSCH_STUFEN); 0 = aus.
   */
  nachlauschMs?: number;
  /**
   * Ticket 2503: auf „Hey Vespera" hoeren, ohne die Sprechtaste.
   *
   * ⚠️ Wirkt nur in der nativen Huelle — ein Browser-Tab bekommt im
   *    Hintergrund kein Mikrofon.
   */
  wachwort?: boolean;
  /**
   * Ticket 2420 (20.09.2026): von selbst absenden, ohne Knopfdruck.
   *
   * ⚠️ Beide Vorgaben sind AUS. Eine Fehlausloesung ist teuer — ein zu frueh
   *    gesendeter halber Satz ist aergerlicher als ein Knopfdruck.
   *
   * `sendeWort` ist seit 26.09.2026 auch eine Liste („Senden, Go, Weiter",
   * Ticket 2659); mit ihm gelten die festen Befehle „Verwerfen" und „ins
   * Textfeld" (Ticket 2674, Tabelle in shared/sende-wort.ts).
   *
   * `sendeWort` wirkt nur, wo waehrend des Sprechens schon Text dasteht
   * (Browser, Anruf). Nativ gibt es den nicht — dort traegt `sendeStilleMs`.
   * Gemessen an Martins sieben Tagen: 82 % seiner Zuege sind nativ.
   */
  sendeWort?: string;
  /**
   * Nach so vielen Millisekunden Stille absenden. 0 = aus,
   * STILLE_AUTO_MS = „Auto" (Jev prüft, ob der Satz fertig ist; Ticket 2763).
   */
  sendeStilleMs?: number;
  /**
   * Ticket 2741 (28.09.2026): die Live-Mitschrift beim Sprechen anzeigen.
   * Vorgabe an. Aus blendet nur die ANZEIGE aus — erkannt wird weiter, weil
   * „Auto", das Auslösewort und „schon gesprochen?" den Text brauchen.
   */
  mitschriftZeigen?: boolean;
  /**
   * Ticket 2778 (Stufe 2, 29.09.2026): Jev hört die Verabschiedung. Sagst du
   * „tschüss“, schließt Vespera ab und das Mikro bleibt zu — auch wenn das
   * Modell den Abschied nicht selbst meldet. Vorgabe an, ein Kontowert (der
   * Server liest ihn selbst, server/jev-abschied.ts); wirkt nur für Konto 1.
   */
  abschiedErkennen?: boolean;
  /**
   * Ticket 2624 (30.09.2026): „Hey Vespera“ nur mit meiner Stimme. Ist er an,
   * verwirft der Server eine vom Wach-Wort geöffnete Aufnahme, wenn Stimmabdruck
   * UND Klang zusammen eindeutig eine andere Stimme sagen (server/wachwort-stimme.ts;
   * ein rohes „fremd“ des Abdrucks allein verwirft nie) — im Zweifel geht die
   * Nachricht durch. Vorgabe aus (schnell), ein Kontowert; nur ein
   * ausdrückliches `true` schaltet ein. Ein Schutz gegen versehentliche
   * fremde Stimmen im Raum, kein Sicherheitsmerkmal.
   */
  wachwortNurMeineStimme?: boolean;
  /**
   * Getippte Fragen laut beantworten (Kundenwunsch 15.09.: „getippt heißt
   * geschrieben"). 23.09.2026, Ticket 2511: vorher nur im Browser
   * (`voice-v8.tippen-vorlesen`), jetzt ein Kontowert. 15.10.2026 (Martin:
   * „für alle auf an"): Vorgabe AN; nur ein gespeichertes `false` (Konto oder
   * alter Geräteschlüssel "0") hält still.
   */
  tippenVorlesen?: boolean;
  /**
   * Modell fuer die starken Auswertungen (Spiegel, Buch, Bericht).
   * 23.09.2026, Ticket 2516: Opus oder Sonnet — Fable ist aus der Auswahl.
   */
  starkesModell?: StarkesModell;
  /** 23.09.2026: Denktiefe für „Mein Leben" und den Essenz-Spiegel. */
  tiefeDenkstufe?: TiefeDenkstufe;
  /**
   * 27.09.2026 (Tickets 2657/2671/2678): wie ausführlich Vespera im Chat und
   * an der Sprechtaste antwortet — ultra-kurz, kurz, normal, ausführlich. Ein Kontowert;
   * der Server liest ihn selbst (server/antwort-tiefe.ts), das Werkzeug
   * antwortmodus_setzen und der Schalter unter dem Eingabefeld schreiben ihn.
   */
  antwortTiefe?: AntwortTiefe;
  /**
   * Text-Chat über die Claude-CLI (Max-Abo) MIT Tools statt über die API. Opt-in
   * — robust gegen leere API-Credits, aber langsamer (Opus) und ohne Live-Tool-
   * Cards. Default false → schneller API-Pfad.
   */
  cliChat?: boolean;
  /**
   * Welches Modell im ABO-Weg antwortet (2026-08-27, Martin: "nur Sonnet,
   * Opus und Haiku lassen").
   *
   * "auto" ist das Verhalten seit dem 06.07.: der Server entscheidet je
   * Nachricht — Opus fuer E-Mails, Analysen und Rechnungen, sonst das
   * schnelle Sonnet. Die drei anderen Werte legen es fest.
   *
   * ⚠️ NICHT `model`. Das steuert den API-Weg, der nur einspringt, wenn das
   * Abo scheitert — und es ist das Modell, das der Bot NENNT, wenn man ihn
   * fragt. Zwei verschiedene Dinge, die leicht zu verwechseln sind.
   */
  aboModell?: AboWahl;
  /**
   * Wer Text und PTT beantwortet. Auto = Claude-Abo, sonst Grok, sonst Gemini.
   * grok/gemini überspringen das Abo bewusst. Live-Anruf bleibt Gemini Live.
   */
  modellBevorzugt?: ModellBevorzugt;
  /**
   * Pre.770 (#45): Passives Wissensmodul. Wenn an, recherchiert der Bot bei
   * erkannten Wissenslücken im Hintergrund und legt die Ergebnisse ab. Opt-in
   * — Default false, keine ungefragten Recherche-Kosten.
   */
  passiveKnowledge?: boolean;
  /**
   * Pre.663: Eigene (ElevenLabs-)Stimmen, die der User in den Einstellungen
   * hinzufügt — werden überall in die Stimmen-Picker gemerged (Vorlesen + Bot).
   * Jede: { id (Voice-ID), label (Name), gender, description? }.
   */
  customVoices?: VoiceOption[];
  /** 2026-07-20 (Martin): true = Vorlese-Stimme BEWUSST gewaehlt — der
   *  Server-Override (realtimeVoice -> Chirp-Erzaehler) respektiert das. */
  readVoiceChosen?: boolean;
  /**
   * 21.09.2026 (Ticket 2448, Beta nur fuer Martin): Vorlesen sofort — die
   * Soniox-Vertonung wird durchgereicht, waehrend sie entsteht (erster Ton
   * nach ~0,5 s statt nach der ganzen Vertonung). Seit 25.07. aus, weil
   * Safari damit brach; jetzt je Nutzer schaltbar, mit Nachspiel-Netz in
   * der Warteschlange. Der Server liest denselben Schluessel.
   */
  ttsDirektBeta?: boolean;
  /**
   * 02.10.2026 (Ticket 2919, Stufe 1, Beta nur für Martin): Soniox-Stimmen als
   * PCM-Strom über Web Audio statt als fertige Datei — der erste Ton kommt
   * nach rund 0,4 s statt nach 3,5 s. Vorgabe AUS: solange er aus ist, läuft
   * jedes Byte wie vorher. Der Server liest denselben Schlüssel.
   */
  ttsPcmBeta?: boolean;
  /** Ticket 2738 (Stufe 2): das Vor-Modell spricht bei Aufträgen sofort einen ersten Satz; Vorgabe aus. */
  vorantwortBeta?: boolean;
  /**
   * 06.10.2026 (Ticket 2738): „Kurz Bescheid geben, wenn die Antwort dauert“ — die Warte-Brücke
   * (server/warte-bruecke.ts). Vorgabe an, wirkt nur für Konto 1; nur ein
   * ausdrückliches `false` schaltet ab. Die Regel steht EINMAL in shared/warte-satz.ts.
   */
  warteSatz?: boolean;
  /**
   * 06.10.2026 (Morse-Modul): „Morse im Menü“ — die Lernseite unter „Täglich“. Nur Konto 1, Vorgabe an;
   * nur ein ausdrückliches `false` blendet sie aus. Die Regel steht EINMAL in shared/morse-konto.ts.
   */
  morseLernen?: boolean;
  /**
   * 15.10.2026 (Dreier-Modus): „Entwickler im Chat“ — die Entwickler-Sitzung am Server hört mit und schreibt als eigene Blase.
   * Nur Konto 1, Vorgabe aus; nur ein ausdrückliches `true` gilt (shared/entwickler-im-chat.ts).
   */
  entwicklerImChat?: boolean;
  /** 18.10.2026: Hauptschalter „Entwickler“ — alle drei zugleich; Vorgabe AN (fehlt = an), nur ein ausdrückliches `false` schaltet aus. */
  entwicklerGesamt?: boolean;
  /**
   * 16.10.2026: „Zimmermann im Chat“ — der zweite Entwickler (Panel, Newsletter, Faktura) darf zusätzlich herein; gilt nur, wenn
   * „Entwickler im Chat“ an ist. Nur Konto 1, Vorgabe aus; nur ein ausdrückliches `true` gilt (shared/entwickler.ts).
   */
  zimmermannImChat?: boolean;
  /** 17.10.2026: „Hüter im Chat“ — Server 7 (Sicherungen) darf zusätzlich herein; wie der Zimmermann (shared/entwickler.ts). */
  hueterImChat?: boolean;
  /** 17.10.2026: „Server-Netz“ — Server-Karte (shared/server-netz.ts); nur Konto 1, Vorgabe aus. */
  serverNetz?: boolean;
  /** 17.10.2026: Selbstverbesserung Phase 1 — "aus" (Vorgabe) | "vorschlagen"; nur Konto 1 (shared/selbstverbesserung.ts). */
  selbstverbesserung?: "aus" | "vorschlagen";
  /**
   * 15.10.2026: „Entwickler vorlesen“ — Beiträge des Schmieds (Dreier-Modus) werden mit seiner eigenen Stimme vorgelesen.
   * Nur Konto 1, Vorgabe aus; nur ein ausdrückliches `true` gilt (shared/schmied.ts).
   */
  entwicklerVorlesen?: boolean;
  /** 19.10.2026: Zimmermann und Hüter im Gespräch von selbst vorlesen (Vorgabe an). */
  zusatzVorlesen?: boolean;
  /** 17.10.2026: „Schmied-Stimme im Ganz-Modus“ (shared/schmied.ts); nur Konto 1, Vorgabe aus (stückweise). */
  schmiedStimmeGanz?: boolean;
  /** 15.10.2026: „Stimme des Entwicklers“ — eine Soniox-Stimme aus SCHMIED_STIMMEN; `null` = Vorgabe (shared/schmied.ts). */
  entwicklerStimme?: string | null;
  /** 16.10.2026: „Stimme des Zimmermanns“ — dieselbe Liste wie beim Schmied; `null` = Vorgabe (shared/schmied.ts `stimmeWahlGueltig`). */
  zimmermannStimme?: string | null;
  /** 17.10.2026: „Stimme des Hüters“ — dieselbe Liste; `null` = Vorgabe. */
  hueterStimme?: string | null;
  /** 17.10.2026: Sprechtempo (0,8–1,6, Schritt 0,05) und Ausführlichkeit je Entwickler; `null` = Vorgabe (shared/schmied.ts). */
  entwicklerTempo?: number | null;
  zimmermannTempo?: number | null;
  hueterTempo?: number | null;
  entwicklerAusfuehrlichkeit?: Ausfuehrlichkeit | null;
  zimmermannAusfuehrlichkeit?: Ausfuehrlichkeit | null;
  hueterAusfuehrlichkeit?: Ausfuehrlichkeit | null;
  /**
   * 11.10.2026 (Ticket 3077): „Musik ohne Probestück“ — Lieder entstehen gleich ganz, nie erst als Probestück.
   * Je Konto, Vorgabe aus; nur ein ausdrückliches `true` gilt. Die Regel steht EINMAL in shared/musik-ohne-probe.ts.
   */
  musikOhneProbe?: boolean;
  /**
   * 13.10.2026: Testschalter „Antworten im Hintergrund vorlesen“ — erlaubt den nativen Vorleser der iPhone-App, solange die
   * Seite verborgen ist. Je Konto, Vorgabe aus; nur ein ausdrückliches `true` gilt (shared/vorleser-nativ.ts).
   */
  vorleserNativ?: boolean;
  /**
   * 14.10.2026: „Mikro beim Vorlesen offen halten“ (iPhone-App). Ohne Wahl (undefined) gilt AUS: die Seite sendet
   * beim Start AUS an die native Kippe (AN bricht das Vorlesen mitten im Satz ab). AN ist ausdrücklich wählbar (Test).
   */
  mikroOffenBeimVorlesen?: boolean;
  /**
   * 19.10.2026: Notschalter des Mikro-Vorstarts (lib/mikro-vorstart.ts) — geht das Mikro nach einer Antwort von selbst auf, wird es
   * in den letzten ~1,2 s der Antwort geholt. Ohne Wahl (undefined) gilt AN; nur ein ausdrückliches `false` schaltet ab.
   */
  mikroVorstart?: boolean;
  /** 19.10.2026: der leise Bereit-Ton, sobald das von selbst geöffnete Mikro aufnimmt. Ohne Wahl: AN; `false` = kein Ton. */
  bereitTon?: boolean;
  /**
   * 14.10.2026: „Antwortvorschau auf dem Sperrbildschirm“ (iPhone-App, Build 110). Aus = die Mitteilung „Vespera hat
   * geantwortet“ trägt nur „Antwort bereit“; an = zusätzlich die ersten Worte der Antwort. Nur ein ausdrückliches `true` gilt.
   */
  antwortVorschauSperrbildschirm?: boolean;
  /**
   * 07.10.2026: Welche Jahreszeit die Tagesszene über Zuhause zeigt —
   * „auto" nach dem Kalender (Vorgabe) oder fest Frühling, Sommer, Herbst,
   * Winter. Martin: „sodass wir dann wechseln können, je nach Jahreszeit."
   * Liste und Regel: shared/jahreszeit.ts.
   */
  szeneJahreszeit?: JahreszeitWahl;
  /**
   * 02.10.2026 (Ticket 2919, Stufe 0): Soniox spricht das Tempo selbst und
   * ein ffmpeg-Lauf mit fester Verstärkung ersetzt Messlauf + atempo. Klingt
   * anders als atempo — Hörprobe. Vorgabe AUS.
   */
  ttsTempoNativ?: boolean;
  /** 02.10.2026 (Ticket 2919): Soniox `reduce_silence` — kürzt Pausen. Erst nach Hörprobe; Vorgabe AUS. */
  ttsStilleKuerzen?: boolean;
  /**
   * 06.09.2026 (Martin: „Sollte man das nicht als Fallback in den
   * Einstellungen haben?"): Stimme, die liest, wenn die gewählte ausfällt
   * (Soniox-Guthaben leer). null = automatisch (ElevenLabs-Stimme des Servers).
   */
  rueckfallVoiceId?: string | null;
  /**
   * 24.09.2026, Ticket 2556 (Martin: *„die Stimme ist zu wach, zu schnell,
   * wirkt eher gestresst"*): Stimme der Morgenmeditation, getrennt von der
   * Vorlese-Stimme. null = ruhige Vorgabe (shared/meditations-stimme.ts).
   */
  meditationVoiceId?: string | null;
  /**
   * 07.10.2026 (Martin: „In erster Linie möchte ich alternativ NUR FÜR DIE
   * MEDITATION die ElevenLabs V4 haben."): die ElevenLabs-Stimme der
   * Meditation, gesprochen mit Eleven v4. null = Soniox wie bisher;
   * `meditationVoiceId` bleibt daneben die Soniox-Stimme und der Rückfall.
   * Wählbar und wirksam nur für Konto 1 (shared/meditations-stimme.ts).
   */
  meditationElevenStimme?: string | null;
  /** Ticket 3438: Stil der Morgenmeditation (shared/meditations-stil.ts); Vorgabe "tag". */
  meditationStil?: MeditationsStil;
  /** Ticket #501: Achtsamkeits-Waechter (Master + Kategorien). */
  achtsamkeit?: {
    aktiv?: boolean;
    kategorien?: Record<string, boolean>;
    /**
     * 2026-07-25 (Martin: "es sollte einstellbar sein, dass er nicht nur
     * still als Notification erscheint, sondern wirklich als Alarm"):
     * Hoerbares Signal zur Meldung. Gleiche Auswahl wie bei Erinnerungen —
     * "still" (Voreinstellung), "gong", "laeuten", "stimme",
     * "laeuten_stimme".
     */
    signal?: string;
  };
}

const STORAGE_KEY = EINSTELLUNGEN_SCHLUESSEL;

/** Pre.663: validiert eine rohe Liste eigener Stimmen (max 30, je gecappt). */
function sanitizeCustomVoices(raw: unknown): VoiceOption[] {
  if (!Array.isArray(raw)) return [];
  const out: VoiceOption[] = [];
  for (const v of raw.slice(0, 30)) {
    if (!v || typeof v !== "object") continue;
    const o = v as Record<string, unknown>;
    const id = typeof o.id === "string" ? o.id.trim().slice(0, 64) : "";
    const label =
      typeof o.label === "string" ? o.label.trim().slice(0, 40) : "";
    if (!id || !label) continue;
    const gender: VoiceGender =
      o.gender === "male" || o.gender === "neutral" ? o.gender : "female";
    const description =
      typeof o.description === "string"
        ? o.description.trim().slice(0, 80)
        : undefined;
    out.push({ id, label, gender, ...(description ? { description } : {}) });
  }
  return out;
}

export const DEFAULT_SETTINGS: VoiceV8Settings = {
  voiceId: VOICE_OPTIONS[0]?.id ?? "",
  // Pre.777: Standard-Erzählerstimme = Google Chirp3-HD-Charon (gratis +
  // zuverlässig). Vorher "" → fiel auf die erste (ElevenLabs-)Stimme zurück.
  // 2026-07-08 (Martin): Default weiblich — frische Geraete pushten sonst
  // Charon (maennlich) per Voll-PUT in die DB ("immer wieder Maennerstimme").
  narratorVoiceId: "de-DE-Chirp3-HD-Aoede",
  hdChartView: "both",
  model: MODEL_OPTIONS[0]?.id ?? "",
  fallbackModel: GEMINI_WERKZEUG,
  language: "de",
  autoConnect: false,
  // 2026-07-05 (Martin): Cloud-TTS respektiert speakingRate jetzt WIRKLICH —
  // die alten 1.2 klangen ploetzlich "extrem schnell". Normal = 1.0.
  speed: 1.0,
  sttSensitivity: "normal",
  responseStyle: "active",
  pttMode: "silent",
  ttsProvider: "auto",
  // Pre.428: Default ist jetzt gemini-realtime — Pipeline hat zu hohe
  // Latenz und Truth-Guard-Probleme im echten Einsatz (Albert-Kündigung).
  voiceMode: "gemini-realtime",
  realtimeVoice: "Aoede",
  realtimeModel: "",
  cliModel: "auto",
  cliExecutor: "server",
  autoEscalate: true,
  qualityMode: "standard",
  heavyModel: "claude-sonnet-4-6",
  useHdInPersona: false,
  emojisInReplies: false,
  stimmungAusStimme: true,
  selbstermaechtigung: true,
  anrufErlaubt: false,
  anrufRuheAb: RUHEZEIT_AB,
  anrufRuheBis: RUHEZEIT_BIS,
  anrufTageslimit: TAGESLIMIT,
  alwaysRecord: false,
  // 30 Tage: die kleinste Zahl, bei der ein Monatsrueckblick noch
  // vollstaendig im sichtbaren Verlauf steht.
  archivTage: 30,
  autoMikroNachFrage: false,
  nachlauschMs: NACHLAUSCH_VORGABE_MS,
  wachwort: false,
  sendeWort: "",
  sendeStilleMs: 0,
  mitschriftZeigen: true,
  abschiedErkennen: true,
  wachwortNurMeineStimme: false,
  tippenVorlesen: true,
  // ⚠️ 23.09.2026: Opus 5.5 statt Fable 5.1, auf Martins Wunsch. Dieselbe
  //    Vorgabe steht in server/strong-model.ts (STARKES_MODELL_VORGABE).
  starkesModell: "opus",
  tiefeDenkstufe: DENKSTUFE_TIEF_VORGABE,
  antwortTiefe: ANTWORT_TIEFE_VORGABE,
  // 2026-07-06: TRUE ist der Normalbetrieb (warme Claude-Sitzung). Der alte
  // false-Default hat bei jedem Settings-Save den Warm-Pfad abgeschaltet.
  cliChat: true,
  aboModell: "auto",
  modellBevorzugt: "auto",
  passiveKnowledge: false,
  customVoices: [],
  readVoiceChosen: false,
  ttsDirektBeta: false,
  ttsPcmBeta: false,
  vorantwortBeta: false,
  warteSatz: true,
  morseLernen: true,
  entwicklerImChat: false,
  entwicklerGesamt: true,
  zimmermannImChat: false,
  hueterImChat: false,
  serverNetz: false,
  selbstverbesserung: "aus",
  entwicklerVorlesen: false,
  zusatzVorlesen: true,
  schmiedStimmeGanz: false,
  entwicklerStimme: null,
  zimmermannStimme: null,
  hueterStimme: null,
  entwicklerTempo: null,
  zimmermannTempo: null,
  hueterTempo: null,
  entwicklerAusfuehrlichkeit: null,
  zimmermannAusfuehrlichkeit: null,
  hueterAusfuehrlichkeit: null,
  musikOhneProbe: false,
  vorleserNativ: false,
  antwortVorschauSperrbildschirm: false,
  szeneJahreszeit: JAHRESZEIT_VORGABE,
  ttsTempoNativ: false,
  ttsStilleKuerzen: false,
  rueckfallVoiceId: null,
  meditationVoiceId: null,
  meditationElevenStimme: null,
  meditationStil: MEDITATIONS_STIL_VORGABE,
};

/**
 * „Getipptes vorlesen“ aus dem Zwischenspeicher: der gespeicherte Kontowert,
 * sonst der alte Geräteschlüssel, sonst die Vorgabe (seit 15.10.2026 AN).
 */
function tippenVorlesenAus(gespeichert: unknown): VoiceV8Settings["tippenVorlesen"] {
  if (typeof gespeichert === "boolean") return gespeichert;
  return leseAltesTippenVorlesen() ?? DEFAULT_SETTINGS.tippenVorlesen;
}

export function loadSettings(): VoiceV8Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      /*
       * ⚠️ 23.09.2026, Ticket 2511: Ein frisches Gerät hat kein Juli-Tempo
       *    zu reparieren. Ohne diese Marke hielte die Einmal-Migration unten
       *    das erste Tempo, das der Server-Abgleich hierher schreibt, für
       *    einen Altwert und setzte es still auf 1,0 zurück.
       */
      try {
        localStorage.setItem("voice-v8.speed-migrated", "1");
      } catch {
        /* localStorage nicht verfuegbar */
      }
      // 15.10.2026: auch ohne gespeicherte Einstellungen gilt ein altes Aus
      // ("0") dieses Geräts — sonst läse es seit der Vorgabe AN laut vor.
      return { ...DEFAULT_SETTINGS, tippenVorlesen: tippenVorlesenAus(undefined) };
    }
    const parsed = JSON.parse(raw) as Partial<VoiceV8Settings>;
    // Einmal-Migration 2026-07-05: alte speed-Werte stammen aus der Zeit, als
    // speakingRate wirkungslos war (fest 1.2) — auf 1.0 zuruecksetzen.
    try {
      if (localStorage.getItem("voice-v8.speed-migrated") !== "1") {
        parsed.speed = 1.0;
        // 2026-07-25 (Martin: "ploetzlich wieder eine Frauenstimme"): das
        // Ergebnis MUSS zurueckgeschrieben werden. Vorher aenderte die
        // Migration nur das zurueckgegebene Objekt — der erste Aufruf lieferte
        // 1.0, jeder weitere wieder den alten Wert. Dadurch war die Migration
        // wirkungslos UND der Start-Abgleich hielt den Unterschied fuer eine
        // Nutzer-Aenderung und schob den alten Browser-Stand (inkl. alter
        // Stimme) in die Datenbank.
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        localStorage.setItem("voice-v8.speed-migrated", "1");
      }
    } catch {
      /* localStorage nicht verfuegbar */
    }
    // Migration: Opus 4.7 → 4.8 (Modell-ID umbenannt) — VOR der Validierung,
    // sonst fiele die alte ID durch die MODEL_OPTIONS-Prüfung raus.
    // 2026-07-25: gespeicherte Auswahl mitziehen (4.7 -> 4.8 -> 5).
    /*
     * ⚠️ 09.09.2026 — STIMMEN-UMZUG. Vier Soniox- und eine ElevenLabs-Stimme
     *    sind aus der Auswahl gefallen (Martin: „Die anderen gefallen mir gar
     *    nicht"). Wer eine davon gespeichert hat — Martin selbst hatte
     *    `soniox:Maya` —, saehe sonst eine leere Auswahl, waehrend im
     *    Hintergrund weiter die alte Stimme spraeche.
     *
     * ⚠️ Das Ergebnis MUSS zurueckgeschrieben werden. Genau daran ist die
     *    Tempo-Migration am 25.07. gescheitert: Sie aenderte nur das
     *    zurueckgegebene Objekt, der naechste Aufruf las wieder den alten
     *    Wert — und der Start-Abgleich hielt das fuer eine Nutzer-Aenderung
     *    und schob die alte Stimme in die Datenbank zurueck.
     */
    let umgezogen = false;
    for (const feld of ["voiceId", "narratorVoiceId", "rueckfallVoiceId"] as const) {
      const alt = parsed[feld];
      if (typeof alt === "string" && STIMMEN_UMZUG[alt]) {
        parsed[feld] = STIMMEN_UMZUG[alt];
        umgezogen = true;
      }
    }
    // 10.09.: dieselbe Bringschuld fuer die Anruf-Stimme (Kore/Puck).
    const altAnruf = parsed.realtimeVoice;
    if (typeof altAnruf === "string" && REALTIME_UMZUG[altAnruf]) {
      parsed.realtimeVoice = REALTIME_UMZUG[altAnruf];
      umgezogen = true;
    }
    if (umgezogen) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      } catch {
        /* localStorage nicht verfuegbar */
      }
    }
    /*
     * Frühere Opus-Kennungen ziehen auf das aktuelle Opus des Registers.
     * ⚠️ 23.09.2026: „claude-opus-5" gehört jetzt mit dazu — sonst bliebe eine
     *    gespeicherte Wahl nach dem Wechsel auf 5.5 still beim alten Modell.
     */
    const altesOpus = /^claude-opus-(4-7|4-8|5)$/;
    if (typeof parsed.model === "string" && altesOpus.test(parsed.model))
      parsed.model = MODELL_STARK;
    if (
      typeof parsed.fallbackModel === "string" &&
      altesOpus.test(parsed.fallbackModel)
    )
      parsed.fallbackModel = MODELL_STARK;
    // 28.09.2026: dasselbe für Sonnet 5 → Register-Sonnet (5.5). Sonst fiele
    //    ein gespeichertes „claude-sonnet-5" durch die Prüfung unten heraus.
    const altesSonnet = /^claude-sonnet-5$/;
    if (typeof parsed.model === "string" && altesSonnet.test(parsed.model))
      parsed.model = MODELL_SCHNELL;
    if (
      typeof parsed.fallbackModel === "string" &&
      altesSonnet.test(parsed.fallbackModel)
    )
      parsed.fallbackModel = MODELL_SCHNELL;
    // 15.10.2026: dasselbe für Haiku 4.5 → Register-Haiku (5.5); sonst zeigte die Fußzeile das alte Haiku weiter.
    const altesHaiku = /^claude-haiku-4-5(?:-\d{8})?$/;
    if (typeof parsed.model === "string" && altesHaiku.test(parsed.model))
      parsed.model = MODELL_GUENSTIG;
    if (
      typeof parsed.fallbackModel === "string" &&
      altesHaiku.test(parsed.fallbackModel)
    )
      parsed.fallbackModel = MODELL_GUENSTIG;
    const sens =
      parsed.sttSensitivity === "quiet" ||
      parsed.sttSensitivity === "normal" ||
      parsed.sttSensitivity === "loud"
        ? parsed.sttSensitivity
        : DEFAULT_SETTINGS.sttSensitivity;
    const style =
      parsed.responseStyle === "active" || parsed.responseStyle === "passive"
        ? parsed.responseStyle
        : DEFAULT_SETTINGS.responseStyle;
    const ptt =
      parsed.pttMode === "silent" ||
      parsed.pttMode === "tts" ||
      parsed.pttMode === "call" ||
      parsed.pttMode === "lock"
        ? parsed.pttMode
        : DEFAULT_SETTINGS.pttMode;
    // Fallback-Modell muss in der MODEL_OPTIONS-Liste sein ODER leer sein.
    // Verhindert dass alte localStorage-Werte mit veralteten IDs den Agent
    // zum Crash bringen.
    // Ticket 2793: eine Gemini-Wahl liest dieselbe Regel wie der Server —
    // ein altes „gemini-3.7-flash" wird 3.8 Flash, nicht still 3.1 Pro.
    const fb =
      typeof parsed.fallbackModel === "string" &&
      /^gemini-/i.test(parsed.fallbackModel)
        ? geminiBackupAus(parsed.fallbackModel)
        : typeof parsed.fallbackModel === "string" &&
            (parsed.fallbackModel === "" ||
              MODEL_OPTIONS.some((m) => m.id === parsed.fallbackModel))
          ? parsed.fallbackModel
          : DEFAULT_SETTINGS.fallbackModel;
    const tp =
      parsed.ttsProvider === "auto" ||
      parsed.ttsProvider === "elevenlabs" ||
      parsed.ttsProvider === "google"
        ? parsed.ttsProvider
        : DEFAULT_SETTINGS.ttsProvider;
    // Als unknown gelesen: im Browser kann noch ein Altwert „fable" liegen,
    // den der Typ nicht mehr kennt (Ticket 2516).
    const cliModell = ohneFable(parsed.cliModel as unknown);
    return {
      voiceId: parsed.voiceId ?? DEFAULT_SETTINGS.voiceId,
      narratorVoiceId:
        parsed.narratorVoiceId ?? DEFAULT_SETTINGS.narratorVoiceId,
      hdChartView:
        parsed.hdChartView === "design" ||
        parsed.hdChartView === "personality" ||
        parsed.hdChartView === "both"
          ? parsed.hdChartView
          : DEFAULT_SETTINGS.hdChartView,
      // 23.09.2026, Ticket 2516: ein gespeichertes „fable" wird Opus —
      // derselbe Leser wie am Server (aboWahlAus), nicht still „auto".
      aboModell: aboWahlAus(parsed.aboModell),
      model: parsed.model ?? DEFAULT_SETTINGS.model,
      fallbackModel: fb,
      language: parsed.language ?? DEFAULT_SETTINGS.language,
      autoConnect: Boolean(parsed.autoConnect),
      // 2026-07-05: Regler ist zurueck — User-Wert gilt wieder. Einmalige
      // Migration setzt Altwerte aus der wirkungslosen Zeit auf 1.0 (oben).
      // 29.09.2026 (Ticket 2800): bis 2,0 und im Raster des Reglers — die
      // Stufen aus 2736 (0,85 / 1,15) rücken dabei auf 0,9 / 1,2.
      speed: vorleseTempo(parsed.speed) ?? DEFAULT_SETTINGS.speed,
      sttSensitivity: sens,
      responseStyle: style,
      pttMode: ptt,
      ttsProvider: tp,
      // Pre.972: nur noch Erzähl-Modus (pipeline) ODER Real Talk (gemini-realtime).
      // Ein alt-gespeicherter 'openai-realtime'-Wert fällt hier auf den Default
      // (= gemini-realtime, siehe DEFAULT_SETTINGS) zurück.
      voiceMode:
        parsed.voiceMode === "gemini-realtime" ||
        parsed.voiceMode === "pipeline"
          ? parsed.voiceMode
          : DEFAULT_SETTINGS.voiceMode,
      // 10.09.: abgelegte Anruf-Stimme -> Nachfolger (gleiches Geschlecht).
      realtimeVoice:
        typeof parsed.realtimeVoice === "string" && parsed.realtimeVoice !== ""
          ? (REALTIME_UMZUG[parsed.realtimeVoice] ?? parsed.realtimeVoice)
          : DEFAULT_SETTINGS.realtimeVoice,
      // Pre.972: realtimeModel gegen die (3.1-only) Liste härten — ein alt
      // gespeicherter 2.5-native-audio-Wert fällt auf '' (= Provider-Default 3.1)
      // zurück, sonst liefe der Agent auf einem entfernten 2.5-Modell.
      realtimeModel:
        typeof parsed.realtimeModel === "string" &&
        (parsed.realtimeModel === "" ||
          GEMINI_REALTIME_MODELS.some((m) => m.id === parsed.realtimeModel))
          ? parsed.realtimeModel
          : DEFAULT_SETTINGS.realtimeModel,
      // Pre.381: CLI-Modell. 23.09.2026, Ticket 2516: ein altes „fable"
      // wird Opus (ohneFable), statt auf „auto" zurückzufallen.
      cliModel:
        cliModell === "haiku" ||
        cliModell === "sonnet" ||
        cliModell === "opus" ||
        cliModell === "auto"
          ? cliModell
          : DEFAULT_SETTINGS.cliModel,
      // Pre.393: CLI-Executor (Server vs. lokaler Mac).
      cliExecutor: parsed.cliExecutor === "desktop" ? "desktop" : "server",
      // Pre.476: Smart-Router.
      autoEscalate:
        typeof parsed.autoEscalate === "boolean"
          ? parsed.autoEscalate
          : DEFAULT_SETTINGS.autoEscalate,
      qualityMode:
        parsed.qualityMode === "premium" || parsed.qualityMode === "standard"
          ? parsed.qualityMode
          : DEFAULT_SETTINGS.qualityMode,
      heavyModel:
        typeof parsed.heavyModel === "string" && parsed.heavyModel.length > 0
          ? parsed.heavyModel
          : DEFAULT_SETTINGS.heavyModel,
      useHdInPersona:
        typeof parsed.useHdInPersona === "boolean"
          ? parsed.useHdInPersona
          : DEFAULT_SETTINGS.useHdInPersona,
      emojisInReplies: istEmojiStufe(parsed.emojisInReplies)
        ? parsed.emojisInReplies
        : DEFAULT_SETTINGS.emojisInReplies,
      stimmungAusStimme:
        typeof parsed.stimmungAusStimme === "boolean"
          ? parsed.stimmungAusStimme
          : DEFAULT_SETTINGS.stimmungAusStimme,
      // Ticket 2550: ohne diese Zeile wäre der Schalter nach jedem Laden weg.
      selbstermaechtigung:
        typeof parsed.selbstermaechtigung === "boolean"
          ? parsed.selbstermaechtigung
          : DEFAULT_SETTINGS.selbstermaechtigung,
      // Ticket 2728: Vorgabe aus — nur ein gespeichertes `true` erlaubt Anrufe.
      anrufErlaubt:
        typeof parsed.anrufErlaubt === "boolean"
          ? parsed.anrufErlaubt
          : DEFAULT_SETTINGS.anrufErlaubt,
      // Ticket 2728: ungültig oder fehlend → Vorgabe (dieselbe Prüfung wie der Server).
      anrufRuheAb: anrufStunde(parsed.anrufRuheAb) ?? DEFAULT_SETTINGS.anrufRuheAb,
      anrufRuheBis: anrufStunde(parsed.anrufRuheBis) ?? DEFAULT_SETTINGS.anrufRuheBis,
      anrufTageslimit: anrufTageslimit(parsed.anrufTageslimit) ?? DEFAULT_SETTINGS.anrufTageslimit,
      // Ticket 745: Altbestand aus dem localStorage einmalig uebernehmen,
      // damit ein bestehender Haken beim Umzug in die DB nicht verlorengeht.
      alwaysRecord:
        typeof parsed.alwaysRecord === "boolean"
          ? parsed.alwaysRecord
          : leseAltenAlwaysRecord(),
      // Ticket 3048: ein gespeichertes "immer" wird still zu "jev" (shared/mikro-nach-antwort.ts).
      autoMikroNachFrage: autoMikroStufe(parsed.autoMikroNachFrage) ?? DEFAULT_SETTINGS.autoMikroNachFrage,
      nachlauschMs: nachlauschFenster(parsed.nachlauschMs),
      wachwort:
        typeof parsed.wachwort === "boolean"
          ? parsed.wachwort
          : DEFAULT_SETTINGS.wachwort,
      /*
       * ⚠️⚠️ 23.09.2026 — starkesModell STAND HIER NICHT.
       *
       * loadSettings gibt eine ausdrückliche Liste zurück; was darin fehlt,
       * ist nach dem Laden weg. Die Auswahl „Modell für die tiefen
       * Auswertungen" zeigte darum nach JEDEM Laden die Vorgabe — während
       * der Server den gespeicherten Wert fuhr (PUT führt zusammen, statt zu
       * ersetzen). Anzeige und Wirklichkeit liefen auseinander, und das bei
       * genau der Einstellung, die Martin jetzt „sichtbar" haben wollte.
       */
      // 23.09.2026, Ticket 2516: „fable" wird Opus — derselbe Leser wie
      // am Server (starkesModellAus), damit die Anzeige zeigt, was läuft.
      starkesModell:
        starkesModellAus(parsed.starkesModell) ??
        DEFAULT_SETTINGS.starkesModell,
      tiefeDenkstufe: istTiefeDenkstufe(parsed.tiefeDenkstufe)
        ? parsed.tiefeDenkstufe
        : DEFAULT_SETTINGS.tiefeDenkstufe,
      // 27.09.2026: fehlt sie hier, zeigte der Schalter nach jedem Laden
      // „normal“ — dieselbe Falle wie oben bei starkesModell.
      antwortTiefe: istAntwortTiefe(parsed.antwortTiefe)
        ? parsed.antwortTiefe
        : DEFAULT_SETTINGS.antwortTiefe,
      sendeWort:
        typeof parsed.sendeWort === "string"
          ? parsed.sendeWort
          : DEFAULT_SETTINGS.sendeWort,
      // 30.09.2026 (Ticket 2842): ein gespeichertes 4 oder 5 s gilt als 3 s (Ticket 3176: Werte ohne Knopf rücken auf die nächstkürzere Stufe, über 60 s auf 60 s).
      sendeStilleMs:
        typeof parsed.sendeStilleMs === "number" &&
        Number.isFinite(parsed.sendeStilleMs)
          ? sendeStilleLesen(parsed.sendeStilleMs)
          : DEFAULT_SETTINGS.sendeStilleMs,
      mitschriftZeigen:
        typeof parsed.mitschriftZeigen === "boolean"
          ? parsed.mitschriftZeigen
          : DEFAULT_SETTINGS.mitschriftZeigen,
      // Ticket 2778: ohne diese Zeile zeigte der Schalter nach jedem Laden „an“.
      abschiedErkennen:
        typeof parsed.abschiedErkennen === "boolean"
          ? parsed.abschiedErkennen
          : DEFAULT_SETTINGS.abschiedErkennen,
      // Ticket 2624: ohne diese Zeile zeigte der Schalter nach jedem Laden „aus“.
      wachwortNurMeineStimme:
        typeof parsed.wachwortNurMeineStimme === "boolean"
          ? parsed.wachwortNurMeineStimme
          : DEFAULT_SETTINGS.wachwortNurMeineStimme,
      cliChat:
        typeof parsed.cliChat === "boolean"
          ? parsed.cliChat
          : DEFAULT_SETTINGS.cliChat,
      modellBevorzugt:
        parsed.modellBevorzugt === "grok" ||
        parsed.modellBevorzugt === "grok-schnell" ||
        parsed.modellBevorzugt === "gemini"
          ? parsed.modellBevorzugt
          : DEFAULT_SETTINGS.modellBevorzugt,
      passiveKnowledge:
        typeof parsed.passiveKnowledge === "boolean"
          ? parsed.passiveKnowledge
          : DEFAULT_SETTINGS.passiveKnowledge,
      customVoices: sanitizeCustomVoices(parsed.customVoices),
      readVoiceChosen: parsed.readVoiceChosen === true,
      ttsDirektBeta: parsed.ttsDirektBeta === true,
      ttsPcmBeta: parsed.ttsPcmBeta === true,
      vorantwortBeta: parsed.vorantwortBeta === true,
      // 06.10.2026: Vorgabe an — ohne diese Zeile zeigte der Schalter nach jedem Laden die Vorgabe.
      warteSatz: typeof parsed.warteSatz === "boolean" ? parsed.warteSatz : DEFAULT_SETTINGS.warteSatz,
      morseLernen: typeof parsed.morseLernen === "boolean" ? parsed.morseLernen : DEFAULT_SETTINGS.morseLernen,
      musikOhneProbe: parsed.musikOhneProbe === true,
      entwicklerImChat: parsed.entwicklerImChat === true,
      entwicklerGesamt: parsed.entwicklerGesamt === undefined || parsed.entwicklerGesamt === true, // fremder Wert = aus (wie am Server)
      zimmermannImChat: parsed.zimmermannImChat === true,
      hueterImChat: parsed.hueterImChat === true,
      serverNetz: parsed.serverNetz === true,
      selbstverbesserung: parsed.selbstverbesserung === "vorschlagen" ? "vorschlagen" : "aus",
      entwicklerVorlesen: parsed.entwicklerVorlesen === true,
      zusatzVorlesen: parsed.zusatzVorlesen !== false,
      schmiedStimmeGanz: parsed.schmiedStimmeGanz === true,
      entwicklerStimme:
        typeof parsed.entwicklerStimme === "string" && schmiedStimmeAus(parsed.entwicklerStimme) === parsed.entwicklerStimme
          ? parsed.entwicklerStimme
          : null,
      zimmermannStimme: typeof parsed.zimmermannStimme === "string" && stimmeWahlGueltig(parsed.zimmermannStimme) ? parsed.zimmermannStimme : null,
      hueterStimme: typeof parsed.hueterStimme === "string" && stimmeWahlGueltig(parsed.hueterStimme) ? parsed.hueterStimme : null,
      entwicklerTempo: tempoWahlGueltig(parsed.entwicklerTempo) ? parsed.entwicklerTempo : null,
      zimmermannTempo: tempoWahlGueltig(parsed.zimmermannTempo) ? parsed.zimmermannTempo : null,
      hueterTempo: tempoWahlGueltig(parsed.hueterTempo) ? parsed.hueterTempo : null,
      entwicklerAusfuehrlichkeit: ausfuehrlichkeitWahlGueltig(parsed.entwicklerAusfuehrlichkeit) ? parsed.entwicklerAusfuehrlichkeit : null,
      zimmermannAusfuehrlichkeit: ausfuehrlichkeitWahlGueltig(parsed.zimmermannAusfuehrlichkeit) ? parsed.zimmermannAusfuehrlichkeit : null,
      hueterAusfuehrlichkeit: ausfuehrlichkeitWahlGueltig(parsed.hueterAusfuehrlichkeit) ? parsed.hueterAusfuehrlichkeit : null,
      vorleserNativ: parsed.vorleserNativ === true,
      antwortVorschauSperrbildschirm: parsed.antwortVorschauSperrbildschirm === true,
      mikroOffenBeimVorlesen: typeof parsed.mikroOffenBeimVorlesen === "boolean" ? parsed.mikroOffenBeimVorlesen : undefined,
      mikroVorstart: typeof parsed.mikroVorstart === "boolean" ? parsed.mikroVorstart : undefined,
      bereitTon: typeof parsed.bereitTon === "boolean" ? parsed.bereitTon : undefined,
      szeneJahreszeit: istJahreszeitWahl(parsed.szeneJahreszeit)
        ? parsed.szeneJahreszeit
        : DEFAULT_SETTINGS.szeneJahreszeit,
      ttsTempoNativ: parsed.ttsTempoNativ === true,
      ttsStilleKuerzen: parsed.ttsStilleKuerzen === true,
      /*
       * ⚠️ 23.09.2026, Ticket 2511 — DIESE VIER FEHLTEN HIER. Was diese
       *    Liste nicht nennt, ist nach dem Laden weg: Die Seite zeigte
       *    „30 Tage", „automatisch" und „alles an", während der Server etwas
       *    anderes fuhr. Beim Achtsamkeits-Wächter schickte die Seite darum
       *    nur Teilobjekte — und der flache Merge löschte am Server Signal
       *    und Kategorien. Seither prüft ein Test, dass jedes Feld
       *    zurückkommt (tests/frontend/einstellungen-zwei-geraete.test.ts).
       */
      archivTage:
        typeof parsed.archivTage === "number" && Number.isFinite(parsed.archivTage)
          ? tageBereinigen(parsed.archivTage)
          : DEFAULT_SETTINGS.archivTage,
      rueckfallVoiceId:
        typeof parsed.rueckfallVoiceId === "string" && parsed.rueckfallVoiceId.trim()
          ? parsed.rueckfallVoiceId
          : null,
      meditationVoiceId: istMeditationsStimme(parsed.meditationVoiceId)
        ? parsed.meditationVoiceId
        : null,
      // 07.10.2026: dieselbe Prüfung wie der Filter am Server (eine ElevenLabs-Kennung oder nichts).
      meditationElevenStimme: istElevenStimmKennung(parsed.meditationElevenStimme)
        ? parsed.meditationElevenStimme
        : null,
      meditationStil: meditationsStilAus(parsed.meditationStil),
      tippenVorlesen: tippenVorlesenAus(parsed.tippenVorlesen),
      ...(parsed.achtsamkeit !== undefined
        ? { achtsamkeit: achtsamkeitAus(parsed.achtsamkeit) }
        : {}),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/** Pre.663: eigene Stimmen des Users (validiert). */
function getCustomVoices(): VoiceOption[] {
  return sanitizeCustomVoices(loadSettings().customVoices);
}

/**
 * Pre.663: ALLE ElevenLabs-Stimmen = eingebaute + eigene. Eine Quelle für alle
 * Picker, damit eigene Stimmen überall auftauchen.
 */
export function getElevenVoices(): VoiceOption[] {
  return [...VOICE_OPTIONS, ...getCustomVoices()];
}

/**
 * Pre.685: Gender-gematchte Google-Chirp3-HD-Fallback-Stimme für eine beliebige
 * Erzähler-/Vorlese-Stimme.
 *
 * Hintergrund: Wenn der User eine ElevenLabs-Stimme (z.B. „Charlotte", weiblich)
 * wählt und ElevenLabs gerade nicht verfügbar ist (Quota erschöpft → 401/429),
 * fällt der Server auf eine Google-Stimme zurück. Bisher war das HARTKODIERT
 * männlich (de-DE-Chirp3-HD-Charon) → der User wählte weiblich, hörte aber
 * männlich. Hier ermitteln wir das Geschlecht der gewählten Stimme und liefern
 * die passende Chirp3-HD-Stimme.
 *
 * Quellen für das Geschlecht (in Reihenfolge):
 *   1) Ist die Stimme schon eine Google-Voice (Chirp3-HD/Wavenet/Neural2) →
 *      kein Fallback nötig, gib sie unverändert zurück.
 *   2) Metadaten: eingebaute + eigene ElevenLabs-Stimmen, Google-Erzähler.
 *   3) Letzter Notnagel: Name-Heuristik (Aoede/Kore/Leda/Zephyr = weiblich;
 *      Charon/Fenrir/Puck/Orus = männlich).
 *   4) Wirklich unbekannt → weiblich-Default (de-DE-Chirp3-HD-Aoede).
 */
const GOOGLE_HD_FEMALE = "de-DE-Chirp3-HD-Aoede";
const GOOGLE_HD_MALE = "de-DE-Chirp3-HD-Charon";

function genderFallbackVoice(gender: VoiceGender): string {
  // „neutral" (eigene Klon-Stimme) hat kein klares Pendant → weiblich-Default.
  return gender === "male" ? GOOGLE_HD_MALE : GOOGLE_HD_FEMALE;
}

export function googleHdFallbackForVoice(voiceId: string): string {
  const id = (voiceId ?? "").trim();
  // Schon eine Google-Stimme → unverändert durchreichen (kein Fallback nötig).
  if (/Chirp3-HD|Wavenet|Neural2/i.test(id)) return id;

  // Metadaten durchsuchen: eingebaute + eigene ElevenLabs + Google-Erzähler.
  const all: ReadonlyArray<VoiceOption> = [
    ...VOICE_OPTIONS,
    ...getCustomVoices(),
    ...GOOGLE_NARRATOR_VOICES,
  ];
  const match = all.find((v) => v.id === id);
  if (match) return genderFallbackVoice(match.gender);

  // Name-Heuristik als letzter Notnagel (deckt Realtime-Namen mit ab).
  if (/\b(Aoede|Kore|Leda|Zephyr|Achernar|Sarah|Laura|Charlotte)\b/i.test(id)) {
    return GOOGLE_HD_FEMALE;
  }
  if (
    /\b(Charon|Fenrir|Puck|Orus|Achird|Algenib|George|Charlie|Liam|Brian)\b/i.test(
      id,
    )
  ) {
    return GOOGLE_HD_MALE;
  }

  // Wirklich unbekannt → weiblich-Default (User-Wunsch).
  return GOOGLE_HD_FEMALE;
}

/** Der rohe Zwischenspeicher, so wie er im Browser liegt (ohne Vorgaben). */
export function liesEinstellungenRoh(): Record<string, unknown> {
  try {
    const roh = localStorage.getItem(STORAGE_KEY);
    if (!roh) return {};
    const p: unknown = JSON.parse(roh);
    return p && typeof p === "object" && !Array.isArray(p)
      ? (p as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function schreibeRoh(roh: Record<string, unknown>): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roh));
    return true;
  } catch {
    /* localStorage might be disabled in private mode */
    return false;
  }
}

/** Woher eine Änderung kommt — der Hörer entscheidet danach (neu zeichnen?). */
type AenderungsQuelle = "lokal" | "server";

function meldeGeaendert(quelle: AenderungsQuelle, felder: string[]): void {
  // Pre.186: storage-Event feuert NICHT im selben Tab. Custom-Event,
  // damit z.B. die Footer-Status-Bar (Modus-Anzeige) sofort updatet.
  try {
    window.dispatchEvent(
      new CustomEvent("voice-v8:settings-changed", { detail: { quelle, felder } }),
    );
  } catch {
    /* CustomEvent unsupported (sehr alter Browser) — egal */
  }
}

/**
 * Werte vom Server übernehmen — NUR lokal, ohne Rückweg zum Server.
 * (23.09.2026)
 *
 * ⚠️ saveSettings schickt immer auch an den Server. Beim Start-Abgleich
 *    würde das ein Echo erzeugen: was gerade vom Server kam, ginge sofort
 *    wieder hin. Hier wird nur der lokale Stand nachgezogen.
 *
 * 23.09.2026, Ticket 2511: der EINZIGE Weg, auf dem Serverwerte in den
 * Zwischenspeicher kommen (einstellungen-abgleich.ts). `entfernen` nimmt
 * Felder heraus, die der Server nicht kennt — nach einem Kontowechsel
 * stünde sonst der Wert des Vorgängers da. Gibt die geänderten Felder zurück.
 */
export function uebernimmVomServer(
  werte: Record<string, unknown>,
  entfernen: readonly string[] = [],
): string[] {
  // Erst die Einmal-Migrationen (sie schreiben zurück) — sonst hielte die
  // Tempo-Migration den eben übernommenen Serverwert für einen Altwert.
  loadSettings();
  const roh = liesEinstellungenRoh();
  const felder: string[] = [];
  for (const [feld, wert] of Object.entries(werte)) {
    if (wert === undefined || JSON.stringify(roh[feld]) === JSON.stringify(wert)) continue;
    roh[feld] = wert;
    felder.push(feld);
  }
  for (const feld of entfernen) {
    if (feld in roh) {
      delete roh[feld];
      felder.push(feld);
    }
  }
  if (felder.length === 0 || !schreibeRoh(roh)) return [];
  meldeGeaendert("server", felder);
  return felder;
}

/**
 * Eine Einstellung ändern: sofort hier, dann am Server — mit Antwort.
 * (23.09.2026, Ticket 2511)
 *
 * Genommen wird nur, was sich gegenüber dem Zwischenspeicher WIRKLICH
 * ändert. Aufrufer geben oft ein ganzes Objekt (`{...loadSettings(), x}`);
 * ginge das ganze Objekt mit frischem Zeitstempel an den Server, würde ein
 * veralteter Schnappschuss (eine lange offene Einstellungsseite) Felder
 * zurückschreiben, die ein anderes Gerät inzwischen geändert hat.
 *
 * Die Oberfläche wartet NICHT: Zwischenspeicher, Warteschlange und Ereignis
 * kommen sofort und synchron; die Antwort des Servers kommt als Promise (und
 * als Ereignis `voice-v8:settings-sync` für alle, die zuhören).
 */
export function speichereEinstellungen(
  neu: Partial<VoiceV8Settings>,
): Promise<SpeicherErgebnis> {
  const alt = loadSettings() as unknown as Record<string, unknown>;
  const roh = liesEinstellungenRoh();
  const geaendert: Record<string, unknown> = {};
  for (const [feld, wert] of Object.entries(neu)) {
    if (wert === undefined) continue;
    if (JSON.stringify(alt[feld]) === JSON.stringify(wert)) continue;
    geaendert[feld] = wert;
    roh[feld] = wert;
  }
  const felder = Object.keys(geaendert);
  if (felder.length === 0) return Promise.resolve(ergebnis("gespeichert", []));
  schreibeRoh(roh);
  merkeAenderungen(geaendert);
  meldeGeaendert("lokal", felder);
  return sendeVorgemerktes(felder);
}

/** Wie speichereEinstellungen, für Aufrufer, die die Antwort nicht brauchen. */
export function saveSettings(s: Partial<VoiceV8Settings>): void {
  void speichereEinstellungen(s);
}
